import { AdminOrganizations } from '@platform/client';
import { OrganizationApplicationStatusEnum } from '@platform/client/schemas';
import {
  InputDropdownItem,
  FormsInputDropdown,
  FormsCheckBox,
  FormsMultilineInput,
} from '@platform/components';
import { useState, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AdminTableContext } from '../../AdminTable';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { useForm, SubmitHandler } from 'react-hook-form';

type UpdateAppStatusModalProps = {
  onClose: () => void;
  organizationId: number;
  applicationId: number;
  status: OrganizationApplicationStatusEnum;
};

type UpdateStatusFormData = {
  newStatus: OrganizationApplicationStatusEnum;
  sendEmail: boolean;
  notes?: string;
};

export const UpdateAppStatusModal = (props: UpdateAppStatusModalProps) => {
  const { onClose, organizationId, status, applicationId } = props;
  const { pagination } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { control, handleSubmit, watch, formState } = useForm<UpdateStatusFormData>({
    defaultValues: {
      newStatus: undefined,
      sendEmail: false,
      notes: undefined,
    },
  });
  const sendEmailSelected = watch('sendEmail');
  const selectedNewStatus = watch('newStatus');
  const queryClient = useQueryClient();
  const statusUpdateMutator = AdminOrganizations.useUpdateOrganizationApplicationStatus({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminOrganizations.getGetOrganizationsQueryKey({
              page: pagination.pageIndex,
              limit: pagination.pageSize,
            }),
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [...AdminOrganizations.getGetReviewDataQueryKey(organizationId)],
        });
        setModalStage('success');
      },
    },
  });
  const onSubmit: SubmitHandler<UpdateStatusFormData> = (data) => {
    if (data.newStatus) {
      statusUpdateMutator.mutate({
        data: {
          applicationId,
          organizationId,
          status: data.newStatus,
          sendEmail:
            data.newStatus !== OrganizationApplicationStatusEnum.pending ? data.sendEmail : false,
          notes: data.notes ?? null,
        },
      });
    }
  };
  return (
    <AdminFormModal
      modalStage={modalStage}
      title={`Org ${organizationId} application status change`}
      onClose={onClose}
      submitCTA={{ text: 'Update status', callback: () => handleSubmit(onSubmit)() }}
    >
      <div className='flex flex-col gap-2'>
        <p className='mb-2'>
          <span className='font-semibold'>Current status:</span> {status.split('_').join(' ')}
        </p>
        <FormsInputDropdown
          type='default'
          label='New application status'
          control={control}
          name='newStatus'
          items={resultItems}
          placeholder='Select a result'
          rules={{ required: 'New status is required' }}
          error={formState.errors.newStatus}
          inputDisabled
        />
        {selectedNewStatus !== OrganizationApplicationStatusEnum.pending && (
          <FormsCheckBox
            size='sm'
            control={control}
            name='sendEmail'
            description='Notify organization'
          />
        )}
        {selectedNewStatus === 'info_requested' && sendEmailSelected && (
          <FormsMultilineInput
            label='Application status notes'
            control={control}
            name='notes'
            rules={{ required: 'Notes required to send this notification' }}
            formsError={formState.errors.notes}
          />
        )}
      </div>
    </AdminFormModal>
  );
};

const resultItems: InputDropdownItem[] = Object.values(OrganizationApplicationStatusEnum).map(
  (status) => ({
    textValue: status,
    text: status.split('_').join(' '),
  }),
);
